import React, {useCallback, useEffect, useState} from 'react';
import axios from 'axios';
import {observer} from 'mobx-react';
import {Keyboard, Navigation} from 'swiper/modules';
import {Swiper, SwiperSlide} from 'swiper/react';
import 'swiper/css';
import store from '../../s';
import {logger} from '../../../logger';
import {currency, getAntiForgeryToken} from '../../../util';
import {IRecommendationSeed, IRecommendedProduct, mergeRecommendations, recommendationSeeds} from './ab-redesign-recommend-list';
import RelatedProductItemImage from '../../../product/components/related-products/image-fallback/related-product-item-image';
import {hasRelatedProductImage} from '../../../product/components/related-products/image-fallback/related-product-image';

/**
 * Redesign arm of the cart step's recommendations: the product page's "We also recommend" row, fed by the same
 * /api/rec_prod store-scoped list (best sellers of the cart line's store plus the state's seal frames) and drawn as the
 * same image / title / price tiles. The control keeps its coordinating-frame row (coord.tsx).
 */

async function fetchRecommendations(seed: IRecommendationSeed): Promise<IRecommendedProduct[]> {
    try {
        // The action answers the JSON as text; axios parses it when it can and leaves "" (no list) as a string.
        const r = await axios.post('/api/rec_prod', {StoreID: seed.StoreID, ProductID: seed.ProductID, GradSchoolID: 0, hideInstName: false}, {headers: {RequestVerificationToken: getAntiForgeryToken()}});
        const data = typeof r.data === 'string' && r.data.trim().startsWith('[') ? JSON.parse(r.data) : r.data;
        return Array.isArray(data) ? data : [];
    } catch (err) {
        logger.error(err);
        return [];
    }
}

const RecommendRow = observer(() => {
    const items = store.checkout.cart.items;
    const [products, setProducts] = useState<IRecommendedProduct[]>([]);
    // products whose every image URL failed to load - they are not shown at all
    const [noImageIds, setNoImageIds] = useState<number[]>([]);
    // Refetch only when the stores/products behind the list change (a qty change re-renders without a new request).
    const seedKey = JSON.stringify(recommendationSeeds(items));

    useEffect(() => {
        const seeds: IRecommendationSeed[] = JSON.parse(seedKey);
        if (seeds.length < 1) {
            setProducts([]);
            return undefined;
        }
        let cancelled = false;
        Promise.all(seeds.map(fetchRecommendations)).then(lists => {
            if (cancelled) {
                return;
            }
            const inCart = (store.checkout.cart.items ?? []).map(o => o.productId);
            setNoImageIds([]);
            setProducts(mergeRecommendations(lists, inCart).filter(p => hasRelatedProductImage(p.image_url, p.image_url_th)));
        });
        return () => {
            cancelled = true;
        };
    }, [seedKey]);

    const handleImageUnavailable = useCallback((productId: number) => {
        setNoImageIds(prev => (prev.includes(productId) ? prev : [...prev, productId]));
    }, []);

    // After the last line is removed the row goes with it rather than keep recommending for an empty cart.
    const cartIds = (items ?? []).map(o => o.productId);
    const visible = products.filter(p => !noImageIds.includes(p.ProductID) && !cartIds.includes(p.ProductID));
    if (!items?.length || visible.length < 2) {
        return null;
    }

    return (
        <div className="coord ab-recommend">
            <div className="coord-heading">We also recommend</div>
            <RecommendSwiper
                products={visible}
                onImageUnavailable={handleImageUnavailable}
            />
        </div>
    );
});

function RecommendSwiper({products, onImageUnavailable}: {products: IRecommendedProduct[]; onImageUnavailable: (productId: number) => void}) {
    const prevRef = React.useRef<HTMLButtonElement>(null);
    const nextRef = React.useRef<HTMLButtonElement>(null);
    const swiperRef = React.useRef<any>(null);

    // Swiper initializes during render, before these buttons exist, so the
    // navigation module bails out. Bind the buttons once they are mounted.
    React.useEffect(() => {
        const swiper = swiperRef.current;
        const prevEl = prevRef.current;
        const nextEl = nextRef.current;
        if (!swiper || swiper.destroyed || !prevEl || !nextEl) {
            return;
        }
        const navigation = swiper.params.navigation;
        if (!navigation || typeof navigation === 'boolean') {
            return;
        }
        navigation.prevEl = prevEl;
        navigation.nextEl = nextEl;
        swiper.navigation.destroy();
        swiper.navigation.init();
        swiper.navigation.update();

        return () => {
            if (!swiper.destroyed) {
                swiper.navigation.destroy();
            }
        };
    }, [products]);

    return (
        <div className="coord-swiper">
            <Swiper
                modules={[Navigation, Keyboard]}
                keyboard={{enabled: true}}
                watchOverflow={true}
                // Fixed-width tiles as on the product page (277px, 200px on phones - ab-redesign-recommend.scss).
                slidesPerView="auto"
                spaceBetween={20}
                breakpoints={{
                    0: {spaceBetween: 8},
                    768: {spaceBetween: 20},
                }}
                navigation={{prevEl: null, nextEl: null}}
                onSwiper={swiper => {
                    swiperRef.current = swiper;
                }}
                aria-roledescription="carousel"
                aria-label="Recommended products"
            >
                {products.map(p => (
                    <SwiperSlide key={p.ProductID}>
                        <a
                            href={p.url}
                            className="ab-rec-item"
                            aria-label={`${p.ProductTitle}, ${currency(p.price)}`}
                        >
                            <RelatedProductItemImage
                                product={{id: p.ProductID, title: p.ProductTitle, src: p.image_url, srcFallback: p.image_url_th}}
                                onUnavailable={onImageUnavailable}
                            />
                            <div className="ab-rec-title">{p.ProductTitle}</div>
                            <div className="ab-rec-price">{currency(p.price)}</div>
                        </a>
                    </SwiperSlide>
                ))}
            </Swiper>
            <button
                type="button"
                ref={prevRef}
                className="coord-nav coord-nav-prev"
                aria-label="Previous products"
            >
                <i
                    className="fas fa-chevron-left"
                    aria-hidden={true}
                />
            </button>
            <button
                type="button"
                ref={nextRef}
                className="coord-nav coord-nav-next"
                aria-label="Next products"
            >
                <i
                    className="fas fa-chevron-right"
                    aria-hidden={true}
                />
            </button>
        </div>
    );
}

export default RecommendRow;
